import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedZone } from '../../redux/slices/activeMechanicalTabSlice';
import { selectGeneralStation, selectJobDetails } from '../../redux/selectors';
import { enqueueApiCall } from '../../redux/slices/apiQueueSlice';
import { setZones } from '../../redux/slices/zoneCountSlice';
import { fetchTableIds } from '../../redux/slices/tableIdSlice';
import { buildZoneCreationPayload, checkCanCreateZone2 } from '../../functions/useCreateZoneData';
import { usePermissions } from '../../hooks/usePermissions';
import { mergeFunctionKeyHeaders } from '../../axios/functionKey';

function ZoneButtons({ activeTabNumber }) {
  const dispatch = useDispatch();
  const generalStationDetails = useSelector(selectGeneralStation);
  const jobDetailsState = useSelector(selectJobDetails);
  const apiUrls = useSelector((state) => state.apiUrls);
  const jobId = useSelector((state) => state.jobId.jobId);
  const { canEdit } = usePermissions();
  const canCreateZone2 = canEdit('portal.zone-buttons.create-zone2');
  
  // Get the selected zone from Redux store
  const selectedZone = useSelector((state) => state.activeMechanicalTab.selectedZone);
  
  // Use Redux state for active zone, fallback to prop if not available
  const currentActiveZone = selectedZone || activeTabNumber || 1;
  
  // Get the zone count from Redux store, safely check if it exists
  const zoneCountData = useSelector((state) => state.zoneCount); 
  const zoneCount = zoneCountData ? zoneCountData.zones : null;
  
  const [showModal, setShowModal] = useState(false);
  const [creatingZone, setCreatingZone] = useState(false);
  const [logs, setLogs] = useState([]);
  const [apiStatus, setApiStatus] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [zone2ExistsState, setZone2ExistsState] = useState(null); // null = checking, true = exists, false = doesn't exist
  
  // Zone count is now populated by fullJobFetchSlice when JobHandler fetches the full job
  // No need to fetch separately here
  
  // Check if Zone 2 already exists in Redux store
  const hasZone2InStore = generalStationDetails.some(detail => detail.Zone === 2);
  
  // Combined check: use API state if available, fallback to store data
  const hasZone2 = zone2ExistsState !== null ? zone2ExistsState : hasZone2InStore;
  
  // Show Create Zone 2 button only when zone count is exactly 1
  const showCreateZone2Button = zoneCount === 1;
  
  // Check Zone 2 existence on component mount or jobId change
  // Only run for Admin/Apps users who can actually create zones
  useEffect(() => {
    const checkZone2Existence = async () => {
      // Only check if the user can create Zone 2.
      if (!canCreateZone2) {
        return;
      }
      
      if (jobId && apiUrls.generalProject && apiUrls.generalStation) {
        try {
          // Pass jobDetailsState as first parameter
          const result = await checkCanCreateZone2(
            jobDetailsState,
            apiUrls.generalProject, 
            apiUrls.generalStation, 
            jobId
          );
          // If Zone 2 already exists, set zone2ExistsState to true
          if (!result.canCreate && result.reason.includes("Zone 2 already exists")) {
            setZone2ExistsState(true);
          } else {
            setZone2ExistsState(false);
          }
        } catch (error) {
          console.error("Error checking Zone 2 existence:", error);
          // Fallback to store data if API check fails
          setZone2ExistsState(null);
        }
      }
    };

    checkZone2Existence();
  }, [jobId, apiUrls.generalProject, apiUrls.generalStation, canCreateZone2, jobDetailsState]);

  // Ensure Zone 1 is selected by default if no zone is currently selected
  useEffect(() => {
    if (jobId && (!selectedZone || selectedZone === 0)) {
      dispatch(setSelectedZone(1));
    }
  }, [jobId, selectedZone, dispatch]);
  
  const handleZoneChange = (zoneNumber) => {
    dispatch(setSelectedZone(zoneNumber));
  };
  
  const handleCreateZoneClick = async () => {
    if (!canCreateZone2) {
      return;
    }

    // Use comprehensive check for Zone 2 creation
    const generalProjectUrl = apiUrls.generalProject;
    const generalStationUrl = apiUrls.generalStation;
    // Pass jobDetailsState as first parameter
    const canCreateResult = await checkCanCreateZone2(
      jobDetailsState,
      generalProjectUrl, 
      generalStationUrl, 
      jobId
    );
    
    if (!canCreateResult.canCreate) {
      setLogs([
        "Cannot create Zone 2:",
        canCreateResult.reason
      ]);
      setShowModal(true);
      return;
    }
    
    setShowModal(true);
    setLogs([
      `Zone creation validation passed.`,
      `General info records: ${canCreateResult.generalInfoCount}`,
      `Existing zones: [${canCreateResult.existingZones.join(', ') || 'None'}]`,
      "Proceeding with Zone 2 creation..."
    ]);
    setApiStatus({});
  };
  
  const closeModal = async () => {
    // If we're closing after showing a "Zone 2 already exists" error, 
    // update the component state to reflect this
    if (logs.length > 0 && logs[0].includes("Cannot create Zone 2") && 
        logs.some(log => log.includes("Zone 2 already exists"))) {
      setZone2ExistsState(true);
    }
    
    setShowModal(false);
    setLogs([]);
    setApiStatus({});
    setShowSuccessMessage(false);
  };
  
  const renderStatusIcon = (status) => {
    if (status === "loading") {
      return <Spinner animation="border" size="sm" />;
    } else if (status === "success") {
      return <span style={{ color: "green" }}>✔️</span>;
    } else if (status === "failed") {
      return <span style={{ color: "red" }}>❌</span>;
    }
    return null;
  };
  
  const createZoneData = async () => {
    if (!jobId) {
      setLogs(prev => [...prev, "Error: No job ID found"]);
      return;
    }
    
    // Double-check with comprehensive validation before proceeding
    const generalProjectUrl = apiUrls.generalProject;
    const generalStationUrl = apiUrls.generalStation;
    // Pass jobDetailsState as first parameter
    const canCreateResult = await checkCanCreateZone2(
      jobDetailsState,
      generalProjectUrl, 
      generalStationUrl, 
      jobId
    );
    
    if (!canCreateResult.canCreate) {
      setLogs(prev => [
        ...prev,
        "Cannot proceed with Zone 2 creation:",
        canCreateResult.reason
      ]);
      return;
    }
    
    setCreatingZone(true);
    setLogs(prev => [...prev, "Starting Zone 2 creation process..."]);
    
    try {
      // Find zone 1 data to use as template
      const zone1Data = generalStationDetails.find(detail => detail.Zone === 1) || {};
      
      if (!zone1Data) {
        setLogs(prev => [...prev, "Error: No Zone 1 data found to copy from"]);
        setCreatingZone(false);
        return;
      }
      
      // Define all API endpoints that need new zone 2 data
      const selectedApiUrls = [
        { url: apiUrls.generalStation, data: { Zone: 2 } },
        { url: apiUrls.hcRequirements, data: { Zone: 2 } },
        { url: apiUrls.skidConstruction, data: { Zone: 2, HasSkid: false } },
        { url: apiUrls.pumpManifoldPiping, data: { Zone: 2, HasPiping: false } },
        { url: apiUrls.stationEnclosure, data: { Zone: 2, HasEnclosure: false } },
        { url: apiUrls.dropPipeComments, data: { Zone: 2, HasDropPipes: false } },
        { url: apiUrls.levelControls, data: { Zone: 2, HasLevelControls: false } },
        { url: apiUrls.pumps, data: { Zone: 2, HasSustain: false, HasMain: false, HasJockey: false, HasWaterFeature: false, HasTransfer: false, HasRecirc: false, HasTrash: false } },
        { url: apiUrls.skyHarvester, data: { Zone: 2, HasSky: false } },
        { url: apiUrls.shipLoose, data: { Zone: 2, HasShipLoose: false } },
        { url: apiUrls.waterTreatment, data: { HasInletFilter: false, HasInletStrainer: false, HasDischargeFilter: false, HasWyeStrainer: false, HasRO: false, HasUV: false, Zone: 2, HasFilters: false } },
        { url: apiUrls.inletScreen, data: { Zone: 2 } },
        { url: apiUrls.inletStrainer, data: { Zone: 2 } },
        { url: apiUrls.dischargeStrainer, data: { Zone: 2 } },
        { url: apiUrls.dischargeFilter, data: { Zone: 2 } },
        { url: apiUrls.roSystem, data: { Zone: 2 } },
        { url: apiUrls.unregulatedPower, data: { Zone: 2, HasUnregulated: false } },
        { url: apiUrls.regulatedPower, data: { Zone: 2, HasRegulated: false } },
        { url: apiUrls.sequenceOfOperations, data: { Zone: 2 } },
        { url: apiUrls.electricalOptions, data: { Zone: 2 } },
        { url: apiUrls.telemetry, data: { Zone: 2, HasTelemetry: false, HasAccessories: false } },
        { url: apiUrls.uvSystem, data: { Zone: 2 } },
        { url: apiUrls.fertigation, data: { Zone: 2, HasFertigation: false } }
      ];
      
      // Process each API endpoint
      setLogs(prev => [...prev, `Creating Zone 2 data for ${selectedApiUrls.length} API endpoints...`]);
      
      for (const { url, data } of selectedApiUrls) {
        const apiName = url.split('/').pop();
        setApiStatus(prev => ({ ...prev, [apiName]: "loading" }));
        setLogs(prev => [...prev, `Creating data for ${apiName}...`]);
        
        try {
          // Use promise to handle async operations
          await new Promise((resolve, reject) => {
            dispatch(enqueueApiCall(async () => {
              try {
                const response = await fetch(url, {
                  method: "POST",
                  headers: mergeFunctionKeyHeaders({
                    "Content-Type": "application/json",
                  }, url),
                  body: JSON.stringify(buildZoneCreationPayload(zone1Data, jobId, 2, data)),
                });
                
                if (response.ok) {
                  setApiStatus(prev => ({ ...prev, [apiName]: "success" }));
                  setLogs(prev => [...prev, `Successfully created data for ${apiName}`]);
                  resolve();
                } else {
                  setApiStatus(prev => ({ ...prev, [apiName]: "failed" }));
                  setLogs(prev => [...prev, `Failed to create data for ${apiName}`]);
                  reject(new Error(`Failed to create data for ${apiName}`));
                }
              } catch (error) {
                setApiStatus(prev => ({ ...prev, [apiName]: "failed" }));
                setLogs(prev => [...prev, `Error creating data for ${apiName}: ${error.message}`]);
                reject(error);
              }
            }));
          });
        } catch (error) {
          console.error(`Error during API call for ${apiName}:`, error);
          // Continue with the next API even if one fails
        }
      }
      
      // Update zone count to 2 (since we just created Zone 2) and refresh table IDs
      dispatch(setZones(2));
      dispatch(fetchTableIds(jobId));
      
      setLogs(prev => [...prev, "Zone 2 creation completed successfully!"]);
      setShowSuccessMessage(true);
      
      // Update state to reflect Zone 2 creation
      setZone2ExistsState(true);
      
      // Auto-close modal after success
      setTimeout(() => {
        closeModal();
        // Switch to the newly created Zone 2
        handleZoneChange(2);
      }, 3000);
      
    } catch (error) {
      console.error("Error creating zone data:", error);
      setLogs(prev => [...prev, `Error creating zone data: ${error.message}`]);
    } finally {
      setCreatingZone(false);
    }
  };
  
  return (
    <>
      <Row className="m-2">
        <Col>
          <Button
            variant={currentActiveZone === 1 ? "primary" : "secondary"}
            onClick={() => handleZoneChange(1)}
          >
            Zone 1
          </Button>
          
          {hasZone2 ? (
            <Button
              variant={currentActiveZone === 2 ? "primary" : "secondary"}
              onClick={() => handleZoneChange(2)}
              className="ms-2"
            >
              Zone 2
            </Button>
          ) : (
            showCreateZone2Button && canCreateZone2 && (
              <Button 
                variant="outline-primary" 
                onClick={handleCreateZoneClick} 
                className="create-zone-2-button ms-2"
              >
                Create Zone 2
              </Button>
            )
          )}
        </Col>
      </Row>
      
      {/* Zone Creation Modal */}
      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Zone 2</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showSuccessMessage && (
            <Alert variant="success">
              Zone 2 created successfully! Switching to Zone 2 view...
            </Alert>
          )}
          
          {logs.length > 0 && logs[0].includes("Cannot create Zone 2") && (
            <Alert variant="warning">
              <strong>Zone Creation Blocked</strong>
              <br />
              {logs.map((log, index) => (
                <div key={index}>{log}</div>
              ))}
            </Alert>
          )}
          
          {(!logs.length || !logs[0].includes("Cannot create Zone 2")) && (
            <p>
              This will create Zone 2 data for this job. The system will verify that Zone 2 
              doesn't already exist and that the maximum record limit hasn't been reached 
              before creating the required data structures with default settings.
            </p>
          )}
          
          {creatingZone && (
            <div className="text-center my-3">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}
          
          {Object.keys(apiStatus).length > 0 && (
            <div className="mt-3">
              <h5>API Status:</h5>
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                <ul className="list-unstyled">
                  {Object.keys(apiStatus).map((api) => (
                    <li key={api} className="mb-1">
                      {api}... {renderStatusIcon(apiStatus[api])}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          <div className="mt-3">
            <h5>Logs:</h5>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '10px', borderRadius: '4px' }}>
              <ul className="list-unstyled">
                {logs.map((log, index) => (
                  <li key={index} className="mb-1">{log}</li>
                ))}
              </ul>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
          {(!logs.length || !logs[0].includes("Cannot create Zone 2")) && (
            <Button 
              variant="primary" 
              onClick={createZoneData} 
              disabled={creatingZone}
            >
              {creatingZone ? (
                <>
                  <Spinner animation="border" size="sm" /> Creating...
                </>
              ) : (
                "Create Zone 2"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ZoneButtons;
